import { useRef } from 'react';

const MAX_PAGE_BUTTONS = 5;

// Fenêtre glissante de 5 pages centrée sur la page courante, bornée aux extrémités.
function visiblePages(currentPage, totalPages) {
  const count = Math.min(MAX_PAGE_BUTTONS, totalPages);
  let start = Math.max(0, currentPage - 2);
  start = Math.min(start, totalPages - count);
  return Array.from({ length: count }, (_, i) => start + i);
}

function Pagination({ currentPage, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <span className="pagination-info">Page {currentPage + 1} / {totalPages}</span>
      <div className="d-flex gap-1">
        <button type="button" className="btn btn-sm btn-outline-secondary pagination-btn-nav"
          onClick={() => onPage(currentPage - 1)} disabled={currentPage === 0}>‹</button>
        {visiblePages(currentPage, totalPages).map((p) => (
          <button type="button" key={p}
            className={`btn btn-sm pagination-btn-page${p === currentPage ? ' active' : ''}`}
            onClick={() => onPage(p)}>{p + 1}</button>
        ))}
        <button type="button" className="btn btn-sm btn-outline-secondary pagination-btn-nav"
          onClick={() => onPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>›</button>
      </div>
    </div>
  );
}

function InvalidEmails({ emails }) {
  if (emails.length === 0) return null;
  const s = emails.length > 1 ? 's' : '';
  return (
    <div className="invalid-emails-box">
      <div className="invalid-emails-title">
        <i className="bi bi-exclamation-triangle-fill"></i>
        {emails.length} adresse{s} invalide{s} ignorée{s} :
      </div>
      <div className="invalid-emails-list">
        {emails.map(addr => <span key={addr} className="invalid-email-tag">{addr}</span>)}
      </div>
      <div className="invalid-emails-hint">
        L'envoi a été effectué pour les adresses valides. Corrigez ces adresses dans la gestion des abonnés.
      </div>
    </div>
  );
}

function AbonneesTable({ abonnees }) {
  return (
    <table className="table table-sm align-middle mb-0 abonnees-table">
      <thead>
        <tr><th>Nom</th><th>Email</th></tr>
      </thead>
      <tbody>
        {abonnees.map((abonnee) => (
          <tr key={abonnee.idabonnee}>
            <td className="abonnees-td-name">{abonnee.nom} {abonnee.prenom}</td>
            <td className="abonnees-td-email">{abonnee.email}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EnvoiAbonnesModal({ modalId, id, envoi, onSent, envoiLabel }) {
  const closeRef = useRef(null);
  const hasInvalid = envoi.invalidEmails.length > 0;

  const handleSend = async () => {
    const result = await envoi.send(id);
    if (result.isComplete) closeRef.current?.click();
    if (result.isSent) onSent?.(result);
  };

  return (
    <div className="modal fade" id={modalId} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header border-0 pb-1">
            <h5 className="modal-title modal-title-sm">Confirmation d'envoi</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div className="modal-body pt-1 modal-body-abonnees">
            {envoi.isLoading && (
              <div className="modal-loading-overlay">
                <span className="spinner-border spinner-border-sm text-secondary" role="status"></span>
              </div>
            )}
            <div className="mb-3">
              <div className="search-input-wrapper">
                <i className="bi bi-search search-input-icon"></i>
                <input type="text" className="form-control form-control-sm search-input-padded"
                  placeholder="Rechercher par nom ou email…" aria-label="Rechercher un abonné"
                  value={envoi.search} onChange={(e) => envoi.handleSearch(e.target.value, id)} />
              </div>
            </div>
            <p className="text-muted mb-3 abonnees-info">
              {envoiLabel}{" "}
              <strong>{envoi.totalElements} abonné{envoi.totalElements !== 1 ? 's' : ''}</strong>.
            </p>
            <AbonneesTable abonnees={envoi.abonnees} />
            <Pagination currentPage={envoi.currentPage} totalPages={envoi.totalPages}
              onPage={(p) => envoi.loadAbonnees(id, p, envoi.search)} />
          </div>
          <InvalidEmails emails={envoi.invalidEmails} />
          <div className="modal-footer border-0 pt-1">
            <button type="button" ref={closeRef} className="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal"
              onClick={envoi.resetInvalidEmails}>
              {hasInvalid ? 'Fermer' : 'Annuler'}
            </button>
            {!hasInvalid && (
              <button type="button" className="btn-axian-cramoisie" onClick={handleSend}
                disabled={envoi.isSending || envoi.totalElements === 0}>
                {envoi.isSending
                  ? <><span className="spinner-border spinner-border-sm btn-icon-mr" role="status"></span>Envoi…</>
                  : <><i className="bi bi-send btn-icon-mr"></i>Envoyer</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnvoiAbonnesModal;
